import { useQuery } from "@tanstack/react-query";
import { z } from "zod";

import { NIKE_URL } from "@/data/constants/url";
import { useAuth } from "@/features/auth/shared/hooks/use-auth";
import { useLang } from "@/hooks/use-lang";
import { Lang, i18n } from "@/i18n-config";
import { createHttpErrorMessage } from "@/utils/create-http-error-message";

import { timeFrameSchema } from "./get-chart-config-list";

type ChartConfigDetailsParams = {
  symbol: string;
  chartId: string;
  period: z.infer<typeof timeFrameSchema>[number];
  limit: number;
  lang: Lang;
  token: string;
};

const chartDataItemSchema = z.object({
  value: z.number().nullish(),
  time: z.string(),
  label: z.string().nullish(),
});

const valueFormatTypeSchema = z.enum([
  "none",
  "number",
  "percent",
  "currency",
  "large-number",
  "large-currency",
]);

const chartTypeSchema = z.enum(["bar", "line", "area"]);

const chartDataSchema = z.object({
  data: z.array(chartDataItemSchema),
  valueFormatType: valueFormatTypeSchema,
  unit: z.string().nullish(),
  label: z.string(),
  chartType: chartTypeSchema,
  groupType: z.enum(["stack", "group"]).nullish(),
  axis: z.string(),
});

const chartConfigDetailsSchema = z.object({
  data: z.object({
    chartDatas: z.array(chartDataSchema),
    title: z.string(),
  }),
  message: z.string(),
});

export type ChartDataItem = z.infer<typeof chartDataItemSchema>;
export type ChartData = z.infer<typeof chartDataSchema>;
export type ChartConfigDetails = z.infer<
  typeof chartConfigDetailsSchema
>["data"];

export const getChartConfigDetails = async ({
  lang,
  symbol,
  token,
  chartId,
  period,
  limit = 20,
}: ChartConfigDetailsParams): Promise<ChartConfigDetails> => {
  const endpoint = `/charts/${symbol}`;

  if (!symbol || !chartId || !period || !lang || !token) {
    throw new Error(`GET: ${endpoint}\n Missing required params`);
  }
  if (!i18n.locales.includes(lang)) {
    throw new Error(`GET: ${endpoint}\n Invalid lang: ${lang}`);
  }
  if (!["Y", "Q", "4Q"].includes(period)) {
    throw new Error(`GET: ${endpoint}\n Invalid period: ${period}`);
  }

  const headers = new Headers({
    "Accept-Language": lang,
    "X-User-Profile": token,
  });
  const queryParams = new URLSearchParams({
    chartId,
    period,
    limit: limit.toString(),
  });
  const res = await fetch(`${NIKE_URL}${endpoint}?${queryParams}`, {
    method: "GET",
    headers,
  });
  if (!res.ok) {
    const errorMessage = await createHttpErrorMessage(endpoint, res);
    throw new Error(errorMessage);
  }
  return chartConfigDetailsSchema.parse(await res.json()).data;
};

export const useChartConfigDetails = (
  symbol: string,
  chartId: string,
  period: z.infer<typeof timeFrameSchema>[number],
  limit: number = 20,
  enabled: boolean = true
) => {
  const { lang } = useLang();
  const { backendToken } = useAuth();
  return useQuery(
    [
      "chart-config-details",
      lang,
      symbol,
      backendToken,
      chartId,
      period,
      limit,
    ],
    () =>
      getChartConfigDetails({
        lang,
        symbol,
        token: backendToken ?? "",
        chartId,
        period,
        limit,
      }),
    {
      enabled: enabled && !!symbol && !!backendToken && !!chartId && !!period,
      keepPreviousData: true,
    }
  );
};
